import {
  AnswerAsk,
  AnswerCarouselSelectionState,
  AnswerSelectSelectionState,
  AnswerSelectionPath,
} from '../interface'

export interface BuildSelectionPathParams {
  cardId: string
  keyAssignmentAnswers: Record<string, AnswerAsk>
  carouselSelectionState: AnswerCarouselSelectionState
  selectSelectionState: AnswerSelectSelectionState
}

export function buildSelectionPath(
  cardId: string,
  keyAssignmentAnswers: Record<string, AnswerAsk>,
  carouselSelectionState: AnswerCarouselSelectionState,
  selectSelectionState: AnswerSelectSelectionState
): AnswerSelectionPath {
  const carouselResult = buildCarouselSelectionPath(
    cardId,
    keyAssignmentAnswers,
    carouselSelectionState,
    selectSelectionState
  )

  /**
   * 選択肢ありカードの可能性があるAnswerId
   * ・ルートカードが選択肢ありカードの場合
   * ・カルーセルの選択肢に選択肢ありカードがぶら下がっている場合
   */
  const maybeAnswerSelectAnswerIds = [cardId, ...carouselResult]
  const selectResult = buildSelectSelectionPath(
    maybeAnswerSelectAnswerIds,
    keyAssignmentAnswers,
    selectSelectionState
  )

  return [cardId, ...carouselResult, ...selectResult]
}

// プライベート関数だが、テストするためexportしている
export function buildSelectSelectionPath(
  answerIds: string[],
  keyAssignmentAnswers: Record<string, AnswerAsk>,
  selectSelectionState: AnswerSelectSelectionState,
  parentFoundAllAnswerIds: string[] = []
): AnswerSelectionPath {
  const result: AnswerSelectionPath = []

  for (const answerId of answerIds) {
    const answer = keyAssignmentAnswers[answerId]
    if (!answer || result.includes(answerId)) {
      continue
    }

    const selectSelectionData = selectSelectionState[answerId]
    if (answer && selectSelectionData) {
      const maybeAnswerSelectAnswerId = selectSelectionData.currentActionActionId
      const allActionIds = selectSelectionData.actionIds
      if (
        maybeAnswerSelectAnswerId &&
        allActionIds.includes(maybeAnswerSelectAnswerId) &&
        !parentFoundAllAnswerIds.includes(maybeAnswerSelectAnswerId)
      ) {
        result.push(maybeAnswerSelectAnswerId)
        const mergedParentFoundAllAnswerIds = [...result, ...parentFoundAllAnswerIds]
        const childResult = buildSelectSelectionPath(
          [maybeAnswerSelectAnswerId],
          keyAssignmentAnswers,
          selectSelectionState,
          mergedParentFoundAllAnswerIds
        )
        result.push(...childResult)
      }
    }
  }

  return result
}

// プライベート関数だが、テストするためexportしている
export function buildCarouselSelectionPath(
  cardId: string,
  keyAssignmentAnswers: Record<string, AnswerAsk>,
  carouselSelectionState: AnswerCarouselSelectionState,
  selectSelectionState: AnswerSelectSelectionState
) {
  const result: string[] = []

  const rootCarouselIds = findRootCarouselIds(cardId, keyAssignmentAnswers, selectSelectionState)
  if (!rootCarouselIds.length) return result

  rootCarouselIds.forEach(rootCarouselId => {
    result.push(...buildSelectionPathByCarouselId(rootCarouselId, carouselSelectionState))
  })

  return result
}

// テストするためexportしている
export function findRootCarouselIds(
  cardId: string,
  keyAssignmentAnswers: Record<string, AnswerAsk>,
  selectSelectionState: AnswerSelectSelectionState
): string[] {
  const rootCarouselIds = []

  /**
   * ルートカルーセルがあるパターン
   *
   * 1. ルートカードがカルーセル
   * 2. ルートカードが選択肢ありカードで、選択肢にカルーセルがぶら下がっている
   */
  const selectAnswerIds = Object.keys(selectSelectionState).reduce<string[]>((acc, key) => {
    const selectSelectionData = selectSelectionState[key]
    if (selectSelectionData.currentActionActionId) {
      acc.push(selectSelectionData.currentActionActionId)
    }
    return acc
  }, [])
  const allAnswerIds = [cardId, ...selectAnswerIds]
  for (const answerId of allAnswerIds) {
    const answer = keyAssignmentAnswers[answerId]
    if (!answer) {
      continue
    }

    for (const content of answer.contents) {
      if (content.carousel) {
        rootCarouselIds.push(content.carousel.id)
      }
    }
  }

  return rootCarouselIds
}

// プライベート関数だが、テストするためexportしている
export function buildSelectionPathByCarouselId(
  carouselId: string,
  carouselSelectionState: AnswerCarouselSelectionState
): string[] {
  const result: string[] = []
  const carouselPath = carouselSelectionState[carouselId]
  if (!carouselPath) return result

  const currentPageId = carouselPath.currentPageID
  const answerIds = carouselPath.selectionPath[currentPageId]

  answerIds.forEach(answerId => {
    if (Object.keys(carouselSelectionState).includes(answerId)) {
      const childCarousel = carouselSelectionState[answerId]
      const childCarouselSelectionPathIds = childCarousel.selectionPath[childCarousel.currentPageID]
      // カルーセルのId自体もAnswerIdなのでselectionPathに入れる必要がある
      result.push(...childCarouselSelectionPathIds, answerId)
      childCarouselSelectionPathIds.forEach(childCarouselId => {
        result.push(...buildSelectionPathByCarouselId(childCarouselId, carouselSelectionState))
      })
    } else {
      result.push(answerId)
    }
  })
  return result
}
